import PropTypes from 'prop-types'
import { useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'

import theme from 'theme/theme'
import { CurrencyInput } from '@components/currency-input'
import {
  AdasDrawer,
  AdasBox,
  AdasTypography,
  AdasButton,
  AdasPaper,
  AdasTextField,
  AdasButtonGroup,
  AdasFormControl,
  AdasInputLabel,
  AdasSelect
} from '@components/wrapper-components'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { formatInitialPrice } from '../../../utils/utils'
import { useUserStore } from '@caradasstore/UserStore'

export const EditProductPricing = ({
  type,
  title,
  product,
  descriptionLength = 256,
  isOpen,
  onClose,
  handleInputChange,
  onSubmit,
  isNational
}) => {
  const { itemNum, name, price, description, total_price, quantity, category_id } = product
  const isBtnDisabled = !price || !quantity
  const isEdit = product.id
  const [itemType, setItemType] = useState(product.item_type ? product.item_type : 'credit') // update type in FE when BE is done
  const [isValid, setIsValid] = useState({
    price: true,
    quantity: true
  })
  const user = useUserStore((store) => store.currentUser)
  // if you need to fetch product categories dynamically, uncomment the following code block

  // const ccId = useCenterStore((store) => store.ccId)

  // const [productCategories, setProductCategories] = useState([])
  // const [isCategoryDisabled, setIsCategoryDisabled] = useState(false)

  // const fetchProductCategories = useCallback(async () => {
  //   try {
  //     const response = await getProductCategories({ ccid: ccId, type: type.toUpperCase() })
  //     if (response.status === 200) {
  //       const categories = response.data
  //       setProductCategories(categories)

  //       if (categories.length === 1 && type !== 'calibration') {
  //         const singleCategory = categories[0]
  //         handleInputChange({
  //           target: {
  //             name: 'category_id',
  //             value: singleCategory.id
  //           }
  //         })
  //         setIsCategoryDisabled(true)
  //       }
  //     }
  //   } catch (error) {
  //     Logger.error({ message: error, payload: { file: 'ProductForm/fetchProductCategories' } })
  //   }
  // }, [ccId, type])

  // useEffect(() => {
  //   if (isOpen) {
  //     fetchProductCategories()
  //   }
  // }, [isOpen, fetchProductCategories])

  const productCategories = product.category
    ? [{ ...product.category, id: product.category.id, label: product.category.name, value: product.category.id }]
    : []

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    handleInputChange({ key: name, value })
  }
  // Allow only integer values for quantity except for labor type
  const handleLaborQuantityChange = (e) => {
    const value = e.target.value

    // Validate decimal input with max 2 decimal places and max 999.99
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      const numericValue = parseFloat(value)
      if (!isNaN(numericValue) && numericValue > 999.99) return

      handleInputChange({ key: 'quantity', value })
    }
  }

  const handleQuantityChange = (e) => {
    const rawValue = e.target.value
    const value = rawValue.replace(/[^0-9]/g, '').replace(/^0+/, '')

    if (!value) {
      handleInputChange({ key: 'quantity', value: '' })
      return
    }

    const numericValue = parseInt(value, 10)
    if (numericValue <= 999) {
      handleInputChange({ key: 'quantity', value })
    }
  }

  //todo: field validation to be done on api integration
  const handleClose = () => onClose(type)
  return (
    <AdasDrawer open={isOpen} anchor='right' onClose={handleClose}>
      <AdasBox className='drawer-title-container'>
        <AdasTypography variant='h6'>{title}</AdasTypography>
        <AdasButton
          buttonType='iconButton'
          sx={{
            padding: '2px',
            cursor: 'pointer',
            color: theme.palette.info.main
          }}
          onClick={handleClose}
        >
          <CloseIcon fontSize='small' />
        </AdasButton>
      </AdasBox>
      <AdasPaper sx={{ margin: '25px', p: '1rem' }}>
        {type === 'charges/discount' ? (
          <AdasBox sx={{ mb: '20px' }}>
            <AdasTypography variant='overline' sx={{ fontWeight: '500', display: 'block' }}>
              ITEM TYPE
            </AdasTypography>
            <AdasButtonGroup variant='contained'>
              <AdasButton onClick={() => setItemType('credit')} variant={itemType === 'credit' ? 'contained' : 'outlined'}>
                CHARGE
              </AdasButton>
              <AdasButton onClick={() => setItemType('discount')} variant={itemType === 'discount' ? 'contained' : 'outlined'}>
                DISCOUNT
              </AdasButton>
            </AdasButtonGroup>
          </AdasBox>
        ) : (
          <AdasBox
            sx={{
              display: 'flex',
              marginRight: { xs: '.2rem' }
            }}
          >
            <AdasTypography sx={{ textTransform: 'uppercase', mb: '20px' }} variant='body2' color='secondary'>
              <span>{type} ID</span> #{itemNum}
            </AdasTypography>
          </AdasBox>
        )}
        <AdasTextField
          onChange={handleInputChange}
          disabled={true}
          name='name'
          sx={{ marginBottom: '20px' }}
          id='name'
          label='Name'
          variant='outlined'
          value={name}
          fullWidth={true}
          InputLabelProps={{
            shrink: true
          }}
          inputProps={{
            maxLength: 100
          }}
        />
        <AdasFormControl fullWidth sx={{ paddingBottom: '1.5rem' }}>
          <AdasInputLabel id='category_label'>Category</AdasInputLabel>
          <AdasSelect
            labelId='Category'
            id='category'
            name='category_id'
            value={category_id}
            label='Category'
            onChange={handleFieldChange}
            disabled={true}
            options={productCategories}
            // error={touched.category && !valid.category}
          />
        </AdasFormControl>
        {type === 'labor' ? (
          <>
            <CurrencyInput
              value={formatInitialPrice(price)} // update value depending on API
              error={!isValid.price} // update value depending on API
              label='Hourly Rate'
              id='hourlyRate'
              name='price'
              onChange={handleFieldChange}
              fullWidth={true}
              variant='outlined'
              InputLabelProps={{
                shrink: true
              }}
              disabled={!user?.is_admin && isNational ? true : false}
              sx={{ marginBottom: '20px' }}
            />
            <AdasTextField
              onChange={handleLaborQuantityChange}
              name='quantity'
              sx={{ marginBottom: '20px' }}
              id='hours'
              label='Hours'
              variant='outlined'
              value={quantity} // update value depending on API
              error={!isValid.quantity} // update value depending on API
              fullWidth={true}
              InputLabelProps={{
                shrink: true
              }}
            />
          </>
        ) : (
          <>
            <CurrencyInput
              value={formatInitialPrice(price)}
              error={!isValid.price}
              label='Unit Price'
              id='unitPrice'
              name='price'
              onChange={handleFieldChange}
              fullWidth={true}
              variant='outlined'
              InputLabelProps={{
                shrink: true
              }}
              disabled={!user?.is_admin && isNational ? true : false}
              sx={{ marginBottom: '20px' }}
            />
            <AdasTextField
              onChange={handleQuantityChange}
              name='quantity'
              sx={{ marginBottom: '20px' }}
              id='quantity'
              label='Quantity'
              variant='outlined'
              value={quantity}
              error={!isValid.quantity}
              fullWidth={true}
              InputLabelProps={{
                shrink: true
              }}
            />
          </>
        )}
        <CurrencyInput
          value={formatInitialPrice(total_price)}
          label='Total Price'
          id='totalPrice'
          name='totalPrice'
          onChange={handleInputChange}
          fullWidth={true}
          disabled={true}
          variant='outlined'
          InputLabelProps={{
            shrink: true
          }}
          sx={{ marginBottom: '20px' }}
        />
        <AdasTextField
          fullWidth
          name='description'
          label='Description'
          sx={{ marginBottom: '20px' }}
          value={description}
          onChange={handleFieldChange}
          inputProps={{ maxLength: descriptionLength }}
          helperText={`Maximum ${descriptionLength} characters (${descriptionLength - (description?.length || 0)} remaining).`}
          multiline
          rows={4}
          disabled={isNational ? true : false}
        />
        <AdasBox
          sx={{
            display: 'flex',
            justifyContent: type === 'custom' && isEdit ? 'space-between' : 'flex-end',
            alignItems: 'center'
          }}
        >
          {/* Render Delete button only for custom type and edit mode */}
          {type === 'custom' && isEdit && (
            <AdasButton color='error' startIcon={<DeleteOutlineIcon />} sx={{ mr: '8px' }} onClick={() => {}}>
              Delete
            </AdasButton>
          )}
          <AdasBox
            sx={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center'
            }}
          >
            <AdasButton variant='outlined' aria-label='cancel' color='primary' onClick={handleClose}>
              Cancel
            </AdasButton>
            <AdasButton
              type='submit'
              disabled={isBtnDisabled}
              variant='contained'
              aria-label='save'
              color='primary'
              onClick={() => onSubmit({ type, product })}
            >
              Save
            </AdasButton>
          </AdasBox>
        </AdasBox>
      </AdasPaper>
    </AdasDrawer>
  )
}

EditProductPricing.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  product: PropTypes.shape({
    id: PropTypes.string,
    itemNum: PropTypes.number,
    name: PropTypes.string,
    price: PropTypes.number,
    description: PropTypes.string,
    totalPrice: PropTypes.number,
    quantity: PropTypes.number,
    item_type: PropTypes.string
  }).isRequired,
  descriptionLength: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired
}
